import * as Sentry from "@sentry/react";

import { StackFrame } from "@sentry/types";
import sentryRelease from "../artifactInfo.json";

export const sanitizeString = (input: string) =>
  input
    .replace(/EDH-\d{4}-\d{6}/g, "*case_id_redacted*")
    .replace(
      /\b[0-9a-f]{8}\b-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-\b[0-9a-f]{12}\b/g,
      "*order_id_redacted*",
    );

/**
 * Helper function to fully sanitize a Sentry event.
 *
 * The application mainly deals with customers health information.
 * As we want to avoid logging any health information to Sentry at all costs, this
 * function is used to strip out any potentially sensitive data from the error event.
 *
 * It has been shaped with a 'better safe than sorry' mindset, and removes any data that might
 * pose as a risk. As an example, since disclosure-codes are used as ID-values in the HTML
 * we avoid sending any of the HTML-data normally present in the breadcrumb-trail,
 * as these could contain the name of a diagnosis or illness in the ID.
 *
 * @param event Sentry event.
 * @returns A fully sanitized Sentry event.
 */

const sanitizeSentryError = (event: Sentry.Event) => {
  const emptyStackFrames: StackFrame[] = [];
  // Remove stack traces

  const { stacktrace: eventStacktrace, ...eventExtra } = event;
  return {
    ...eventExtra,
    exception: {
      values:
        event.exception &&
        event.exception.values &&
        event.exception?.values.map((val: any) => {
          // Remove stack traces
          const { stacktrace: valStacktrace, ...valRest } = val;
          return {
            ...valRest,
            value:
              "Exception value and stack traces has been redacted to avoid logging health information.",
          };
        }),
    },
    breadcrumbs: event.breadcrumbs?.map((breadcrumb: any) => {
      return {
        ...breadcrumb,
        message:
          "Breadcrumb data has been redacted to avoid logging health information.",
        data: Object.assign({
          message:
            "Breadcrumb data has been redacted to avoid logging health information.",
        }),
      };
    }),
    request: {
      ...event.request,
      url: event.request?.url && sanitizeString(event.request?.url),
    },
  };
};

export const initializeSentry = () => {
  Sentry.init({
    dsn: window.env.SENTRY_DNS,
    release: sentryRelease.sentryRelease,
    environment: window.env.SENTRY_ENVIRONMENT,

    beforeSend(event: any) {
      return sanitizeSentryError(event);
    },
  });
};
